<template>
  <Breadcrumb :items="breadcrumbs" />
  <Loading v-if="loading" />
  <div v-else class="form-page">
    <div class="form-page-header">
      <div class="form-page-header__icon">
        <i class="pi pi-info-circle"></i>
      </div>
      <div class="form-page-header__text">
        <h1 class="form-page-header__title">تعديل صفحة "من نحن"</h1>
        <p class="form-page-header__subtitle">تحرير المحتوى الظاهر في صفحة "من نحن" بالموقع العام</p>
      </div>
    </div>

    <div class="container-fluid">
      <form @submit.prevent="sendForm()">
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-pencil"></i></div>
            <span class="form-card__title">المحتوى</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">عنوان الصفحة <span class="optional">اختياري</span></label>
                  <InputText v-model="title" class="w-100" placeholder="من نحن" />
                </div>
              </div>
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">العنوان الفرعي <span class="optional">اختياري</span></label>
                  <InputText v-model="subtitle" class="w-100" placeholder="جملة قصيرة تحت العنوان الرئيسي" />
                </div>
              </div>
              <div class="col-12">
                <div class="form-field">
                  <label class="field-label">المحتوى <span class="required">*</span></label>
                  <Editor v-model="content" editorStyle="height: 320px" />
                  <span class="field-error" v-if="v$.content.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء ادخال المحتوى
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-compass"></i></div>
            <span class="form-card__title">الرؤية والرسالة</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">الرؤية <span class="optional">اختياري</span></label>
                  <Textarea v-model="vision" rows="4" class="w-100" placeholder="رؤيتنا..." />
                </div>
              </div>
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">الرسالة <span class="optional">اختياري</span></label>
                  <Textarea v-model="mission" rows="4" class="w-100" placeholder="رسالتنا..." />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Statistics -->
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-chart-bar"></i></div>
            <span class="form-card__title">الإحصائيات <span class="optional">اختياري</span></span>
          </div>
          <div class="form-card__body">
            <div v-for="(stat, idx) in statistics" :key="idx" class="answer-item">
              <div class="answer-item__index">{{ idx + 1 }}</div>
              <div class="answer-item__content">
                <div class="stat-row">
                  <InputText v-model="stat.value" placeholder="مثال: 500+" class="w-100" />
                  <InputText v-model="stat.label" placeholder="مثال: طالب متخرج" class="w-100" />
                </div>
              </div>
              <div class="answer-item__actions">
                <button type="button" class="answer-remove-btn" @click="removeStatistic(idx)">
                  <i class="pi pi-trash"></i>
                </button>
              </div>
            </div>

            <button type="button" class="add-answer-btn" @click="addStatistic">
              <i class="pi pi-plus"></i>
              إضافة إحصائية جديدة
            </button>
          </div>
        </div>

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-image"></i></div>
            <span class="form-card__title">صورة الغلاف</span>
          </div>
          <div class="form-card__body">
            <img v-if="currentImageLink && !imageFile" :src="currentImageLink" alt="" class="current-image-preview mb-3" />
            <div :class="['upload-zone', { 'upload-zone--has-file': imageFile }]">
              <div class="upload-zone__icon">
                <i :class="`pi ${imageFile ? 'pi-check' : 'pi-cloud-upload'}`"></i>
              </div>
              <p class="upload-zone__label">{{ imageFile ? imageFile.name : 'اضغط لاختيار صورة' }}</p>
              <p class="upload-zone__hint" v-if="!imageFile">PNG, JPG, GIF</p>
              <input type="file" @change="onImageChange" accept="image/*" />
            </div>
          </div>
        </div>

        <!-- Gallery -->
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-images"></i></div>
            <span class="form-card__title">معرض الصور <span class="optional">اختياري</span></span>
          </div>
          <div class="form-card__body">
            <div class="gallery-grid mb-3" v-if="galleryKeep.length || galleryNewPreviews.length">
              <div v-for="path in galleryKeep" :key="path" class="gallery-thumb">
                <img :src="galleryLinkFor(path)" alt="" />
                <button type="button" class="gallery-thumb__remove" @click="removeKeptImage(path)">
                  <i class="pi pi-times"></i>
                </button>
              </div>
              <div v-for="(preview, idx) in galleryNewPreviews" :key="`new-${idx}`" class="gallery-thumb">
                <img :src="preview" alt="" />
                <button type="button" class="gallery-thumb__remove" @click="removeNewImage(idx)">
                  <i class="pi pi-times"></i>
                </button>
              </div>
            </div>
            <div class="upload-zone">
              <div class="upload-zone__icon"><i class="pi pi-cloud-upload"></i></div>
              <p class="upload-zone__label">اضغط لإضافة صور للمعرض</p>
              <p class="upload-zone__hint">يمكن اختيار أكثر من صورة</p>
              <input type="file" multiple @change="onGalleryChange" accept="image/*" />
            </div>
          </div>
        </div>

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-cog"></i></div>
            <span class="form-card__title">الحالة</span>
          </div>
          <div class="form-card__body">
            <div class="source-toggle">
              <i class="pi pi-eye" style="color: var(--text-secondary);"></i>
              <span class="source-toggle__label">نشر الصفحة</span>
              <InputSwitch v-model="isPublished" />
              <span class="source-toggle__hint">{{ isPublished ? 'ظاهرة في الموقع العام' : 'مسودة غير ظاهرة' }}</span>
            </div>
          </div>
        </div>

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-search"></i></div>
            <span class="form-card__title">تحسين محركات البحث (SEO)</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">عنوان SEO <span class="optional">اختياري</span></label>
                  <InputText v-model="metaTitle" class="w-100" />
                </div>
              </div>
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">وصف SEO <span class="optional">اختياري</span></label>
                  <Textarea v-model="metaDescription" rows="2" class="w-100" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="btn-submit" :disabled="formSending">
            <i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i>
            <i class="pi pi-check" v-else></i>
            {{ formSending ? 'جاري الحفظ...' : 'حفظ التعديلات' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import InputSwitch from 'primevue/inputswitch';
import Editor from 'primevue/editor';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';

export default {
  name: 'AboutEdit',
  components: { Breadcrumb, Loading, InputText, Textarea, InputSwitch, Editor },
  data() {
    return {
      v$: useVuelidate(),
      breadcrumbs: [
        { name: 'الرئيسية', path: '/' },
        { name: 'من نحن', path: '/about' },
        { name: 'تعديل', path: '/about/edit' },
      ],
      title: '',
      subtitle: '',
      content: '',
      vision: '',
      mission: '',
      statistics: [],
      imageFile: null,
      currentImageLink: null,
      // Gallery: existing image paths kept from the loaded record (strings),
      // plus newly-picked File objects awaiting upload on save.
      galleryKeep: [],
      galleryLinks: {}, // path -> full URL, for rendering kept thumbnails
      galleryNewFiles: [],
      galleryNewPreviews: [],
      isPublished: true,
      metaTitle: '',
      metaDescription: '',
      formSending: false,
      loading: true,
    };
  },
  validations() {
    return {
      content: { required },
    };
  },
  methods: {
    onImageChange(e) {
      this.imageFile = e.target.files[0] || null;
    },
    onGalleryChange(e) {
      const files = Array.from(e.target.files || []);
      files.forEach((file) => {
        this.galleryNewFiles.push(file);
        this.galleryNewPreviews.push(URL.createObjectURL(file));
      });
      e.target.value = '';
    },
    removeKeptImage(path) {
      this.galleryKeep = this.galleryKeep.filter((p) => p !== path);
    },
    removeNewImage(idx) {
      this.galleryNewFiles.splice(idx, 1);
      this.galleryNewPreviews.splice(idx, 1);
    },
    galleryLinkFor(path) {
      return this.galleryLinks[path] || path;
    },
    addStatistic() {
      this.statistics.push({ label: '', value: '' });
    },
    removeStatistic(idx) {
      this.statistics.splice(idx, 1);
    },
    async fetchAbout() {
      this.loading = true;
      try {
        const { data } = await axios.get('/admin/about-page');
        const about = data.data;
        this.title = about?.title ?? '';
        this.subtitle = about?.subtitle ?? '';
        this.content = about?.content ?? '';
        this.vision = about?.vision ?? '';
        this.mission = about?.mission ?? '';
        this.statistics = (about?.statistics ?? []).map((s) => ({ label: s.label ?? '', value: s.value ?? '' }));
        this.currentImageLink = about?.image_link ?? null;
        this.galleryKeep = (about?.gallery ?? []).map((g) => g.path);
        this.galleryLinks = Object.fromEntries((about?.gallery ?? []).map((g) => [g.path, g.link]));
        this.isPublished = about?.is_published ?? true;
        this.metaTitle = about?.meta_title ?? '';
        this.metaDescription = about?.meta_description ?? '';
      } finally {
        this.loading = false;
      }
    },
    async sendForm() {
      this.v$.$validate();
      if (this.v$.$error) return;
      this.formSending = true;
      try {
        const fd = new FormData();
        fd.append('title', this.title ?? '');
        fd.append('subtitle', this.subtitle ?? '');
        fd.append('content', this.content ?? '');
        fd.append('vision', this.vision ?? '');
        fd.append('mission', this.mission ?? '');
        this.statistics
          .filter((s) => (s.label ?? '').trim() || (s.value ?? '').trim())
          .forEach((s, i) => {
            fd.append(`statistics[${i}][label]`, s.label ?? '');
            fd.append(`statistics[${i}][value]`, s.value ?? '');
          });
        this.galleryKeep.forEach((path) => fd.append('gallery_keep[]', path));
        this.galleryNewFiles.forEach((file) => fd.append('gallery_new[]', file));
        fd.append('is_published', this.isPublished ? 1 : 0);
        fd.append('meta_title', this.metaTitle ?? '');
        fd.append('meta_description', this.metaDescription ?? '');
        if (this.imageFile) fd.append('image', this.imageFile);

        await axios.post('/admin/about-page', fd);
        Swal.fire({ icon: 'success', title: 'تم حفظ التعديلات بنجاح', timer: 1500, showConfirmButton: false });
        this.$router.push('/about');
      } catch (error) {
        Swal.fire({ icon: 'error', title: error.response?.data?.message || 'حدث خطأ', timer: 2500, showConfirmButton: false });
      } finally {
        this.formSending = false;
      }
    },
  },
  mounted() {
    this.fetchAbout();
  },
};
</script>

<style scoped>
.current-image-preview { max-width: 320px; border-radius: var(--radius-md); border: 1px solid var(--surface-border); display: block; }
.stat-row { display: flex; gap: 0.75rem; }
.stat-row > * { flex: 1; min-width: 0; }
.gallery-grid { display: flex; flex-wrap: wrap; gap: 0.75rem; }
.gallery-thumb { position: relative; width: 96px; height: 96px; border-radius: var(--radius-md); overflow: hidden; border: 1px solid var(--surface-border); }
.gallery-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.gallery-thumb__remove {
  position: absolute; top: 4px; inset-inline-end: 4px; width: 22px; height: 22px; border-radius: 50%;
  background: rgba(0,0,0,.55); color: #fff; border: none; display: flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 0.7rem;
}
</style>
